
import { Component, Vue } from 'vue-property-decorator';
import { USER_GETTERS } from '@/store/user/getters';
import { User } from '@/store/user/types';
import { ContentItem, ContentAssignment, CONTENT_TYPE, Phase } from '@/types';
import { collection, getDocs, query, where, addDoc, getDoc, doc, Timestamp, updateDoc } from 'firebase/firestore';
import { firestore } from '@/firebase';
import { ROOT_ACTIONS } from '@/store/actions';

@Component
export default class Screencasts extends Vue {
  loading = true;
  allContent: ContentItem[] = [];
  userAssignments: ContentAssignment[] = [];
  currentPhase: Phase | null = null;

  get user(): User {
    return this.$store.getters[USER_GETTERS.ACTING_USER];
  }

  get userLanguage(): string {
    return this.user.language || 'nl-be';
  }

  get filteredContent(): ContentItem[] {
    // Filter by language and non-archived
    return this.allContent.filter(item => 
      !item.archived && 
      (item.language === this.userLanguage || item.language === this.userLanguage.substring(0, 2))
    );
  }

  async mounted() {
    this.$store.dispatch(ROOT_ACTIONS.SET_BREADCRUMBS, [
      { text: 'Home', to: '/participant' },
      { text: this.$t('screencasts') || 'Screencasts', to: '/participant/screencasts' }
    ]);

    await this.loadData();
  }

  async loadData() {
    try {
      this.loading = true;

      // Load all content items
      const contentSnapshot = await getDocs(collection(firestore, 'content-items'));
      this.allContent = contentSnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as ContentItem));

      // Load user's existing assignments
      const assignmentsQuery = query(
        collection(firestore, 'content-assignments'),
        where('user', '==', this.user.id)
      );
      const assignmentsSnapshot = await getDocs(assignmentsQuery);
      this.userAssignments = assignmentsSnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id
      } as ContentAssignment));

      // Load user's current phase (for auto-assignment)
      await this.loadCurrentPhase();

    } catch (error) {
      console.error('Error loading screencasts:', error);
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: 'error',
        text: 'Error loading screencasts'
      });
    } finally {
      this.loading = false;
    }
  }

  async loadCurrentPhase() {
    try {
      // Find user's active program
      const programsQuery = query(
        collection(firestore, 'programs'),
        where('participant', '==', this.user.id),
        where('isArchived', '==', false)
      );
      const programsSnapshot = await getDocs(programsQuery);
      
      if (programsSnapshot.empty) return;

      const program = programsSnapshot.docs[0];
      
      // Get phases for the program
      const phasesQuery = query(
        collection(firestore, 'phases'),
        where('programId', '==', program.id)
      );
      const phasesSnapshot = await getDocs(phasesQuery);

      // Find current phase (date-based)
      const now = new Date();
      for (const phaseDoc of phasesSnapshot.docs) {
        const phase = phaseDoc.data() as Phase;
        const start = phase.start.toDate();
        const end = phase.end.toDate();
        
        if (now >= start && now <= end) {
          this.currentPhase = { ...phase, id: phaseDoc.id };
          break;
        }
      }
    } catch (error) {
      console.error('Error loading current phase:', error);
    }
  }

  isCompleted(contentId: string): boolean {
    const assignment = this.userAssignments.find(a => a.contentId === contentId);
    return assignment ? assignment.completed : false;
  }

  getHourValue(contentId: string): number {
    const item = this.allContent.find(c => c.id === contentId);
    return item?.hourValue || 1;
  }

  getIcon(type: CONTENT_TYPE): string {
    switch (type) {
      case CONTENT_TYPE.VIDEO:
        return 'mdi-video';
      case CONTENT_TYPE.YOUTUBE_VIDEO:
        return 'mdi-youtube';
      case CONTENT_TYPE.PDF:
        return 'mdi-file-pdf-box';
      default:
        return 'mdi-file';
    }
  }

  getIconColor(type: CONTENT_TYPE): string {
    switch (type) {
      case CONTENT_TYPE.VIDEO:
        return 'blue';
      case CONTENT_TYPE.YOUTUBE_VIDEO:
        return 'red';
      case CONTENT_TYPE.PDF:
        return 'orange';
      default:
        return 'grey';
    }
  }

  cardClass(contentId: string): string {
    return this.isCompleted(contentId) ? 'completed-card' : '';
  }

  async viewContent(contentId: string) {
    try {
      // Check if assignment already exists
      let assignment = this.userAssignments.find(a => a.contentId === contentId);

      if (!assignment) {
        // Auto-create assignment record for tracking
        const contentItem = this.allContent.find(c => c.id === contentId);
        
        const newAssignment = {
          user: this.user.id,
          contentId: contentId,
          completed: false,
          requestedBy: this.user.id, // Self-assigned
          ...(this.currentPhase?.id && { phase: this.currentPhase.id }), // Only include if exists
          hoursAdded: false,
          // NEW FIELDS for refactored system
          autoCompleteHours: true, // Auto-complete on assignment as per requirements
          viewedAt: Timestamp.now(),
          hourValue: contentItem?.hourValue || 1,
          legacy: false
        };

        const assignmentDoc = await addDoc(collection(firestore, 'content-assignments'), newAssignment);
        
        assignment = {
          ...newAssignment,
          id: assignmentDoc.id
        } as ContentAssignment;

        this.userAssignments.push(assignment);
      } else if (!assignment.viewedAt) {
        // Update viewedAt if not set
        await updateDoc(doc(firestore, 'content-assignments', assignment.id!), {
          viewedAt: Timestamp.now()
        });
      }

      // Navigate to view content
      this.$router.push(`/participant/content/${assignment.id}`);
    } catch (error) {
      console.error('Error creating/updating assignment:', error);
      this.$store.dispatch(ROOT_ACTIONS.TOGGLE_NOTIFICATION, {
        color: 'error',
        text: 'Error opening screencast'
      });
    }
  }
}
